import { createLink, type LinkComponent, type LinkProps } from '@tanstack/react-router';
import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';

import { AppIcons, RailCount, type AppIconName } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

/* ────────────────────────── Item ────────────────────────── */

type ItemBaseProps = {
  icon: AppIconName;
  label: string;
  active?: boolean;
  count?: number;
};

type ItemLinkProps = ItemBaseProps & {
  to: LinkProps['to'];
  params?: LinkProps['params'];
  onClick?: never;
};

type ItemButtonProps = ItemBaseProps & {
  to?: never;
  params?: never;
  onClick: () => void;
};

type NavigationRailItemProps = ItemLinkProps | ItemButtonProps;

const itemClassName = (active: boolean): string =>
  twcn(
    'flex h-row-h w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2 text-left text-meta text-foreground-default no-underline transition-colors duration-fast ease-standard',
    '[&>svg]:flex-shrink-0 [&>svg]:text-muted-foreground',
    active
      ? 'bg-primary-tint font-medium text-primary-ink shadow-inset-primary-soft [&>svg]:text-primary'
      : 'hover:bg-subtle',
  );

type AnchorElProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> & {
  active?: boolean;
};

const ItemAnchor = forwardRef<HTMLAnchorElement, AnchorElProps>(function ItemAnchor(
  { active, children, ...rest },
  ref,
) {
  // className goes AFTER spread so TanStack's injected `className` cannot
  // shadow ours.
  return (
    <a ref={ref} {...rest} className={itemClassName(active ?? false)}>
      {children}
    </a>
  );
});

const ItemLink: LinkComponent<typeof ItemAnchor> = createLink(ItemAnchor);

/**
 * NavigationRail.Item — leaf row in the rail. With `to` it renders a
 * typed router link; otherwise a button (optional `onClick`).
 * `active` lights it up with the terracotta tint; `count` adds a
 * trailing mono badge.
 */
function Item(props: Readonly<NavigationRailItemProps>): React.JSX.Element {
  const { icon, label, active = false, count } = props;

  const content = (
    <>
      <AppIcons name={icon} size={14} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count != null && count > 0 ? <RailCount value={count} active={active} /> : null}
    </>
  );

  if (props.to != null) {
    return (
      <ItemLink to={props.to} params={props.params as never} active={active}>
        {content}
      </ItemLink>
    );
  }

  return (
    <button type="button" onClick={props.onClick} className={itemClassName(active)}>
      {content}
    </button>
  );
}

/* ────────────────────────── Root ────────────────────────── */

interface NavigationRailProps {
  children: ReactNode;
}

/**
 * NavigationRail — primary nav section inside the sidebar. Renders a
 * single `.rail-section` shell (`gap-px px-2`). `NavigationRail.Item`
 * is the canonical leaf, but the rail also accepts arbitrary
 * children (dividers, ad-hoc rows) since not every product surface
 * is a single closed taxonomy.
 */
function NavigationRailRoot({ children }: Readonly<NavigationRailProps>): React.JSX.Element {
  return <div className="flex flex-col gap-px px-2">{children}</div>;
}

export const NavigationRail = Object.assign(NavigationRailRoot, {
  Item,
});
